import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconType } from '@shared/utils/icons';
import { ButtonType } from '@shared/ui/buttons';
import { RangePickerComponent } from '../range-picker/range-picker.component';
import { filter, Subject, takeUntil } from 'rxjs';
import { SearchRangeTerms } from '../../models/search-range-terms.models';
import { DateRangeModel, DateRangeTerm } from '../range-picker/range-picker.models';

@Component({
  selector: 'shared-ui-datetime-range-picker-popup',
  templateUrl: './range-picker-popup.component.html',
  styleUrls: ['./range-picker-popup.component.css'],
  standalone: false,
})
export class RangePickerPopupComponent extends RangePickerComponent implements OnInit, OnDestroy {
  @ViewChild('rangePicker') rangePicker!: RangePickerComponent;

  IconType = IconType;
  ButtonType = ButtonType;

  orderedSearchRangeTerms: SearchRangeTerms[] = Object.values(SearchRangeTerms) as SearchRangeTerms[];

  private ngUnsubscribe = new Subject<void>();

  constructor(cdr: ChangeDetectorRef) {
    super(cdr);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlSearchTerm?.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((x) => {
      this.selectedSearchRangeTerm = x;
    });

    this.formControlVariableName?.valueChanges
      .pipe(
        filter((x) => x !== null && x !== undefined),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((x) => {
        const startDate = new Date(x.startDate);
        const endDate = new Date(x.endDate);

        this.setDateRangeAndFormat(startDate, endDate);
      });
  }

  onInnerDateFilter(event: DateRangeTerm): void {
    if (event?.dateRange?.startDate && event?.dateRange?.endDate) {
      this.rangeDates = [new Date(event.dateRange.startDate), new Date(event.dateRange.endDate)];
      this.selectedSearchRangeTerm = event.searchRangeTerm ?? this.searchRangeTerms.CustomRange;

      this.cdr.detectChanges();
    } else {
      this.rangeDates = [];
      this.cdr.detectChanges();
    }

    this.dateFilter.emit(event);
  }

  quickButtonSelectionClick(selectRange: SearchRangeTerms): void {
    this.reset();
    this.selectedSearchRangeTerm = selectRange;
    const selectedDateRange = this.getDateRange(selectRange);
    this.setDateRangeAndFormat(selectedDateRange.startDate, selectedDateRange.endDate);
    this.rangePicker.updateRange(selectedDateRange.startDate, selectedDateRange.endDate);
    this.emitSelectedDate();
  }

  onTermClicked(term: SearchRangeTerms): void {
    if (term !== this.searchRangeTerms.CustomRange) {
      this.quickButtonSelectionClick(term);
    }
  }

  reset(): void {
    this.rangePicker.clearFilters();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private setDateRangeAndFormat(startDate: Date, endDate: Date): void {
    this.rangeDates = [startDate, endDate];
  }

  private getDateRange(selectRange: SearchRangeTerms): DateRangeModel {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (selectRange) {
      case this.searchRangeTerms.Yesterday:
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
        break;
      case this.searchRangeTerms.LastSevenDays:
        startDate.setDate(today.getDate() - 7);
        break;
      case this.searchRangeTerms.LastThirtyDays:
        startDate.setDate(today.getDate() - 30);
        break;
      case this.searchRangeTerms.ThisMonth:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), this.getMaxPossibleDate().getDate());
        break;
      case this.searchRangeTerms.LastMonth:
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    return new DateRangeModel(startDate, endDate);
  }
}
